import styled from "@emotion/styled";
import { lazy, Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useNavigate, useParams } from "react-router";

import type { ModalDialogProps } from "@skilitics/designer-ui-components";
import { Button, DialogContent, InfoTooltip, ModalDialog } from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

import { ApplicationPreferencesForm } from "./ApplicationPreferencesForm";

const ModuleMapPreferencesForm = lazy(async () => {
  const module = await import("./ModuleMapPreferencesForm");
  return { default: module.ModuleMapPreferencesForm };
});

const Dialog = styled(ModalDialog)`
  --dialog-width: 420px;
`;

const ModuleMapPreferecesErrorBoundary = styled.div`
  color: ${({ theme }) => theme.error};
  padding: 1rem;
`;

export const PreferencesDialog = (props: ModalDialogProps) => {
  const formatMessage = useFormatMessage(formatStrings);
  const { moduleId } = useParams<"moduleId">() as { moduleId: string };
  const navigate = useNavigate();

  return (
    <Dialog
      header={
        <>
          {formatMessage("title")}
          <InfoTooltip tooltip={formatMessage("description")} />
        </>
      }
      buttons="showClose"
      {...props}
    >
      <StyledContent>
        <ApplicationPreferencesForm />
        {moduleId && (
          <ErrorBoundary
            fallback={
              <ModuleMapPreferecesErrorBoundary>
                <p>Something went wrong loading Module Map Preferences.</p>
                <Button onClick={() => navigate(0)} variant="outlined">
                  Refresh Page
                </Button>
              </ModuleMapPreferecesErrorBoundary>
            }
          >
            <Suspense fallback={null}>
              <ModuleMapPreferencesForm />
            </Suspense>
          </ErrorBoundary>
        )}
      </StyledContent>
    </Dialog>
  );
};

const StyledContent = styled(DialogContent)`
  width: var(--dialog-width);
  padding: 20px 25px 0;
`;

const formatStrings = {
  description: {
    en: `A collection of preferences that are stored locally, using local storage, and are not persisted across different browsers or machines.`,
  },
  title: {
    en: "Preferences",
  },
};
